<template>
  <div
    :id="id"
    role="group"
    :aria-label="ariaLabel"
    :class="['ui-checkbox-group', { 'ui-checkbox-group--stacked': stacked }]"
  >
    <UiCheckbox
      v-for="opt in normalizedOptions"
      :key="String(opt.value)"
      :model-value="modelValue"
      :value="opt.value"
      :disabled="disabled || opt.disabled"
      :name="name"
      :size="size"
      @update:model-value="onChildUpdate"
    >
      {{ opt.text }}
    </UiCheckbox>
  </div>
</template>

<script setup lang="ts">
  /**
   * UiCheckboxGroup — Tailwind primitive replacing BootstrapVue-Next's
   * `b-form-checkbox-group`.
   *
   * Renders one <UiCheckbox> per option and shares an array v-model.
   * Matches BVN's prop names (`options`, `stacked`) so existing
   * `<b-form-checkbox-group :options="..." stacked />` call sites swap
   * mechanically.
   *
   * Usage:
   *   <UiCheckboxGroup
   *     v-model="selectedProductTypes"
   *     :options="productTypeData"
   *     stacked
   *   />
   */
  import { computed } from 'vue'

  import UiCheckbox from './UiCheckbox.vue'

  defineOptions({ name: 'UiCheckboxGroup' })

  type Primitive = string | number | boolean
  type RichOption = { value: Primitive, text: string, disabled?: boolean }
  type RawOption = Primitive | RichOption

  interface Props {
    modelValue?: Primitive[]
    options?: RawOption[]
    stacked?: boolean
    disabled?: boolean
    size?: 'sm' | 'md' | 'lg'
    id?: string
    name?: string
    ariaLabel?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: () => [],
    options: () => [],
    stacked: false,
    disabled: false,
    size: 'md',
    id: undefined,
    name: undefined,
    ariaLabel: undefined,
  })

  const emit = defineEmits<{
    'update:modelValue': [value: Primitive[]]
    'change': [value: Primitive[]]
  }>()

  // Same RawOption→RichOption normaliser pattern as UiSelect, with the
  // explicit type predicate to keep vue-tsc's narrowing happy across the
  // primitive-vs-object union.
  function isRichOption (opt: RawOption): opt is RichOption {
    return opt !== null && typeof opt === 'object'
  }

  const normalizedOptions = computed<RichOption[]>(() =>
    (props.options ?? []).map<RichOption>(opt =>
      isRichOption(opt)
        ? { value: opt.value, text: String(opt.text), disabled: opt.disabled === true }
        : { value: opt, text: String(opt) }
    )
  )

  // UiCheckbox already returns the new-array (push/remove) shape, so we
  // can just relay it. Cast through Primitive[] because UiCheckbox's
  // ModelValue union is wider than this group's contract.
  function onChildUpdate (next: unknown) {
    const arr = (Array.isArray(next) ? next : []) as Primitive[]
    emit('update:modelValue', arr)
    emit('change', arr)
  }
</script>

<style scoped>
  .ui-checkbox-group {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.75rem 1rem;
  }
  .ui-checkbox-group--stacked {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }
</style>
